import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HarbourFlyout from '../../components/harbour/HarbourFlyout';
import HarbourButton from '../../components/harbour/HarbourButton';
import HarbourNotice from '../../components/harbour/HarbourNotice';
import HarbourIcon from '../../components/harbour/HarbourIcon';
import HarbourProgressSegments from '../../components/harbour/HarbourProgressSegments';
import HarbourStatusChip from '../../components/harbour/HarbourStatusChip';
import HarbourTextLink from '../../components/harbour/HarbourTextLink';
import HarbourToast from '../../components/harbour/HarbourToast';
import { pathwayGuardian, roleFormChip } from '../../utils/harbourPupils';
import { apiHarbourInviteFamily, apiResendParticipantInvite, apiConfirmSubmission } from '../../utils/rmsApi';
import {
  recordRequiresSubmissionConfirmation,
  isRoleSubmissionConfirmed,
  canConfirmSubmission,
  isReferralPathwayActive,
} from '../../utils/submissionConfirmation';
import ConfirmAndSubmitScreen from '../../components/ConfirmAndSubmitScreen';
import SubmitFormsModal from '../../components/SubmitFormsModal';
import { useRmsState } from '../../context/RmsStateContext';
import {
  harbourFormKeysFor,
  harbourFormRoute,
  harbourFormTitle,
  countSchemaQuestions,
  estimatedMinutes,
  harbourWizardAnsweredCount,
  buildHomeRows,
} from '../../utils/harbourForms';
import '../../components/harbour/harbour.css';

/*
 * School forms homepage (TDI-53 sections 04, 15, 17): the wide flyout listing
 * a pathway's school forms with the sequential lock. Counts and time
 * estimates come from the LIVE schemas — never hardcoded — so the questions
 * stay native to the RMS. One form at a time; finished forms stay open.
 */
/** "3 November 2026" */
function formatLongDate(value) {
  if (!value) return null;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

/**
 * A school form a teacher fills in (TDI-618, IN-13): "{first name} is
 * filling this in" with their progress, or, once done, "Completed by {name},
 * {role}, on {date}" (the date is known once the teacher has left, TDI-617).
 * The SENCO can read a finished form, never start one that is a teacher's.
 */
function TeacherHeldFormRow({ row, index, title, route, tone, submitted }) {
  const teacher = row.heldBy;
  const fullName = [teacher.firstName, teacher.lastName].filter(Boolean).join(' ') || teacher.email || 'A teacher';
  const first = teacher.firstName || fullName;
  const done = row.status === 'done';
  const completedAt = formatLongDate(((teacher.allocations || {})[row.formKey] || {}).completedAt);
  const meta = done
    ? `Completed by ${[fullName, teacher.jobRole].filter(Boolean).join(', ')}${completedAt ? `, on ${completedAt}` : ''}`
    : `${first} is filling this in`;
  return (
    <div
      className={[
        'harbour-form-row',
        `harbour-form-row--${tone}`,
        done ? 'harbour-form-row--done' : '',
      ].filter(Boolean).join(' ')}
    >
      <span className="harbour-form-row__disc" aria-hidden="true">
        {done ? <HarbourIcon name="check" size={16} /> : index + 1}
      </span>
      <span className="harbour-form-row__text">
        <span className="harbour-body-16-bold">{title}</span>
        <span className="harbour-form-row__meta">{meta}</span>
      </span>
      <span className="ml-auto flex items-center gap-3 flex-none">
        <HarbourStatusChip
          status={done ? 'done' : row.status === 'inProgress' ? 'inProgress' : 'notStarted'}
          label={done && submitted ? 'Submitted' : undefined}
          tone={done ? tone : 'neutral'}
          surface={done ? 'onWhite' : 'tinted'}
        />
        {done && route && <HarbourTextLink to={`${route}?mode=review`}>View</HarbourTextLink>}
      </span>
    </div>
  );
}

export default function HarbourFormsHomeFlyout({ pupil, pathway, orgId, onClose, loading = false }) {
  const navigate = useNavigate();
  const { ensurePreSchema } = useRmsState();
  const [schemas, setSchemas] = useState({});

  const firstName = pupil ? pupil.name.split(' ')[0] : '';
  const senActive = Boolean(pupil?.sen && pupil.sen.stage !== 'stopped');
  // The family took their permission back for the SEND pathway (TDI-479):
  // the school's answers are kept and readable, nothing can be added.
  const senWithdrawn = pathway === 'sen'
    && Boolean(pupil?.sen && pupil.sen.stage === 'stopped' && pupil.sen.stopReason === 'consent_withdrawn');
  const formKeys = useMemo(
    () => (pupil
      ? harbourFormKeysFor(pathway, {
          // The record picks the HFP set: v3 for Harbour pupils, v2 otherwise.
          patient: pupil.patient || null,
          intakeConcern: pupil.patient?.intakeConcern || null,
          // A withdrawn SEND pathway still lists its forms, read-only.
          senActive: senActive || senWithdrawn,
          schoolPhase: pupil.patient?.schoolPhase || null,
        })
      : []),
    [pupil, pathway, senActive, senWithdrawn]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(formKeys.map(async (formKey) => {
        try {
          const schema = await ensurePreSchema({ orgId, formKey });
          return [formKey, schema];
        } catch (_) {
          return [formKey, null];
        }
      }));
      if (!cancelled) setSchemas(Object.fromEntries(entries));
    })();
    return () => { cancelled = true; };
  }, [formKeys, ensurePreSchema, orgId]);

  // Forms given to a teacher are theirs (TDI-618, IN-13): the row says who is
  // filling it in, with no Start for the SENCO, and the SENCO's own forms
  // unlock among themselves only. A teacher who has left keeps the forms they
  // finished, so the row can say who completed it (TDI-617).
  const holders = useMemo(() => {
    const out = {};
    for (const t of [...(pupil?.formerTeachers || []), ...(pupil?.teachers || [])]) {
      for (const formKey of Object.keys(t.allocations || {})) out[formKey] = t;
    }
    return out;
  }, [pupil]);
  const rows = useMemo(
    () => buildHomeRows(formKeys, pupil?.formProgress || {}, 'school_user', holders),
    [formKeys, pupil, holders]
  );
  const doneCount = rows.filter((r) => r.status === 'done').length;
  const allDone = rows.length > 0 && doneCount === rows.length;

  // Confirm and submit (TDI-415): records gated on the step offer it here
  // once every form this pathway asks of the school is complete. Each
  // pathway is confirmed on its own and locks only its own forms (TDI-651,
  // Tom King 27 Oct: the two pathways are completely separate); the parent
  // or carer confirms on their own portal.
  const confirmPathway = pathway === 'sen' ? 'sen' : 'referral';
  const patient = pupil?.patient || null;
  const formProgress = pupil?.formProgress || {};
  const needsConfirmStep = recordRequiresSubmissionConfirmation(patient);
  const schoolConfirmed = isRoleSubmissionConfirmed(patient, 'school_user', confirmPathway);
  const canConfirm = needsConfirmStep && !schoolConfirmed && !senWithdrawn
    && canConfirmSubmission({ patient, formProgress, role: 'school_user', pathway: confirmPathway });
  // The SEND pathway submits through the pop-up drawn for it ("Submit your
  // forms · SENCO", 5226:131473); the referral keeps its confirm screen,
  // which carries the consent and nominee statements.
  const usesSubmitPopup = confirmPathway === 'sen';
  // "Submitted" and "locked" are only true once the answers really are. On a
  // record gated on the confirm step they become true at confirmation; on a
  // legacy record completing the forms is the submission, but nothing locks,
  // so that wording is not used there.
  const answersSubmitted = allDone && (!needsConfirmStep || schoolConfirmed);
  const answersLocked = allDone && needsConfirmStep && schoolConfirmed;
  // Read-only for either reason: the confirmed lock, or the family leaving.
  const readOnly = answersLocked || senWithdrawn;
  const [confirming, setConfirming] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmFailed, setConfirmFailed] = useState(false);
  // The forms this pathway's confirmation covers, which are exactly the ones
  // this homepage lists: a referral confirmation never lists (or locks) the
  // SEND pathway's forms, nor the reverse (TDI-651).
  const confirmTitles = useMemo(
    () => formKeys.map((key) => harbourFormTitle(key, schemas[key])),
    [formKeys, schemas]
  );
  // "All forms answered or coming back" (5226:131473): the pop-up opens by
  // itself when the school arrives with every form answered and not yet
  // submitted. "Check my answers first" closes it until they ask again.
  const [submitPopupOpen, setSubmitPopupOpen] = useState(false);
  const [submitPopupShown, setSubmitPopupShown] = useState(false);
  useEffect(() => {
    if (usesSubmitPopup && canConfirm && allDone && !loading && !submitPopupShown) {
      setSubmitPopupOpen(true);
      setSubmitPopupShown(true);
    }
  }, [usesSubmitPopup, canConfirm, allDone, loading, submitPopupShown]);
  // The school's explicit re-confirmation of consent and the nominated parent
  // or carer (Tom King, 22 Oct). The referral wizard records the nomination,
  // so the Request for Referral hides that question — and with it the form's
  // own consent checkbox. The confirm step carries the statements instead,
  // naming the actual people. Only wizard-started referrals (the ones with
  // referralPathway.informant) need them: a form-started referral still shows
  // the nomination question and its checkbox on the form itself.
  const informant = patient?.referralPathway?.informant || null;
  const confirmStatements = useMemo(() => {
    // The statements are about the referral; the SEND pathway carries none.
    if (confirmPathway !== 'referral') return [];
    if (!patient || !informant || !isReferralPathwayActive(patient)) return [];
    const parentName = [informant.firstName, informant.lastName].filter(Boolean).join(' ')
      || 'the parent or carer';
    return [
      {
        key: 'consentConfirmed',
        label: `I confirm that ${parentName} has consented to this referral.`,
      },
      {
        key: 'nomineeConfirmed',
        label: `I confirm that ${parentName}${informant.email ? ` (${informant.email})` : ''} is ${firstName}'s nominated parent or carer and is expecting an email asking them to fill in the referral forms.`,
      },
    ];
  }, [confirmPathway, patient, informant, firstName]);
  const onConfirmSubmission = async () => {
    setConfirmBusy(true);
    setConfirmFailed(false);
    try {
      await apiConfirmSubmission({
        orgId,
        patientRecordId: pupil.id,
        pathway: confirmPathway,
        // The screen only enables the button once every statement is ticked;
        // the server refuses the confirm without them (rmsConfirmSubmission).
        ...(confirmStatements.length > 0
          ? { confirmations: Object.fromEntries(confirmStatements.map((s) => [s.key, true])) }
          : {}),
      });
      setConfirming(false);
      setSubmitPopupOpen(false);
      setToast({ title: 'Your forms are confirmed', body: 'Your answers are submitted and can no longer be changed.' });
    } catch (_e) {
      setConfirmFailed(true);
    } finally {
      setConfirmBusy(false);
    }
  };

  // "What happens next": the parent or carer side, with a gentle reminder.
  const guardian = pupil ? pathwayGuardian(pupil, pathway === 'sen' ? 'sen' : 'referral') : null;
  const guardianStatus = pathway === 'sen' ? pupil?.senGuardianStatus : pupil?.guardianStatus;
  const guardianChip = roleFormChip(guardianStatus);
  // "Freddie's profile will be created when Claire has also finished"
  // (5226:131473): said only while the parent or carer has not submitted
  // their SEND forms, naming them when the record does.
  const guardianSenConfirmed = isRoleSubmissionConfirmed(patient, 'guardian', 'sen');
  const profileWaitLine = pathway === 'sen' && !guardianSenConfirmed
    ? `${firstName}’s profile will be created when ${guardian?.firstName || 'the parent or carer'} has also finished.`
    : null;
  const [toast, setToast] = useState(null);
  const [reminderBusy, setReminderBusy] = useState(false);
  const sendReminder = async () => {
    setReminderBusy(true);
    try {
      if (guardian && guardian.status === 'invite_sent') {
        await apiResendParticipantInvite({
          orgId, patientRecordId: pupil.id,
          participantId: guardian.participantId || guardian.id,
        });
      } else {
        await apiHarbourInviteFamily({
          orgId,
          patientRecordId: pupil.id,
          participantId: guardian ? (guardian.participantId || guardian.id) : undefined,
        });
      }
      setToast({ title: 'Reminder sent', body: `We have emailed ${guardian?.firstName || 'the parent or carer'} their sign-in link again.` });
    } catch (e) {
      setToast({ title: 'That did not work', body: e?.message || 'The reminder could not be sent.' });
    } finally {
      setReminderBusy(false);
    }
  };

  // The questions the school will actually be asked: the referral wizard's
  // answers are not asked again on the Request for Referral.
  const questionCount = (formKey, schema) => {
    const count = countSchemaQuestions(schema, 'school_user');
    if (count == null) return null;
    return Math.max(0, count - harbourWizardAnsweredCount(formKey, pupil?.patient || null, schema)) || null;
  };
  const rowMeta = (formKey) => {
    const schema = schemas[formKey];
    const count = questionCount(formKey, schema);
    const minutes = estimatedMinutes(schema, count);
    return [
      count ? `${count} question${count === 1 ? '' : 's'}` : null,
      minutes ? `about ${minutes} minute${minutes === 1 ? '' : 's'}` : null,
    ]
      .filter(Boolean)
      .join(' · ');
  };
  // "About 65 minutes in total" before anything is done, then the time the
  // unfinished forms still need: "About 52 minutes remaining" (3192:155560).
  const minutesFor = (formKey) => {
    const schema = schemas[formKey];
    return estimatedMinutes(schema, questionCount(formKey, schema)) || 0;
  };
  const totalMinutes = formKeys.reduce((sum, formKey) => sum + minutesFor(formKey), 0);
  const remainingMinutes = rows
    .filter((r) => r.status !== 'done')
    .reduce((sum, r) => sum + minutesFor(r.formKey), 0);
  const timeLine = doneCount === 0
    ? (totalMinutes > 0 ? `About ${totalMinutes} minutes in total. ` : '')
    : (remainingMinutes > 0 ? `About ${remainingMinutes} minutes remaining. ` : '');

  // Until the pupil's own record snapshot lands, formProgress is empty, so
  // every form reads "not started" and the page would announce "0 of N forms
  // done" over a working Start button. Skeletons until we actually know.
  if (loading) {
    return (
      <HarbourFlyout
        width="wide"
        title={pupil?.name || ''}
        year={pupil?.yearGroup}
        eyebrow={{
          tone: pathway === 'sen' ? 'sen' : 'referral',
          label: 'School forms',
          tag: pathway === 'sen' ? 'SEND pathway' : 'Referral pathway',
        }}
        onClose={onClose}
      >
        <div className="harbour-task-body">
          <div className="harbour-task-body__main harbour-forms-home flex flex-col gap-4" style={{ width: 1168 }}>
            <div aria-hidden="true" className="flex flex-col gap-5">
              <div className="harbour-skeleton h-[96px] animate-pulse" />
              <div className="harbour-skeleton h-[72px] animate-pulse" />
              <div className="harbour-skeleton h-[72px] animate-pulse" />
              <div className="harbour-skeleton h-[72px] animate-pulse" />
            </div>
          </div>
        </div>
      </HarbourFlyout>
    );
  }

  return (
    <HarbourFlyout
      width="wide"
      title={pupil?.name || ''}
      year={pupil?.yearGroup}
      eyebrow={{
        tone: pathway === 'sen' ? 'sen' : 'referral',
        label: 'School forms',
        tag: pathway === 'sen' ? 'SEND pathway' : 'Referral pathway',
      }}
      onClose={onClose}
    >
      <div className="harbour-task-body">
        <div className="harbour-task-body__main harbour-forms-home flex flex-col gap-4" style={{ width: 1168 }}>
          {confirming ? (
            <div className="harbour-form-card">
              <ConfirmAndSubmitScreen
                formTitles={confirmTitles}
                confirmations={confirmStatements}
                statementDetail={confirmPathway === 'referral'
                  ? 'The referral is sent to the clinical team once you and the parent or carer have both confirmed.'
                  : 'The profile is shared once you and the parent or carer have both confirmed.'}
                actionKind="primary"
                busy={confirmBusy}
                error={confirmFailed}
                onConfirm={onConfirmSubmission}
                onLater={() => { if (!confirmBusy) setConfirming(false); }}
              />
            </div>
          ) : (
          <div className="harbour-form-card">
            <div className="flex items-center gap-4">
              {allDone ? (
                <span className="flex items-center gap-3">
                  <span
                    className={`harbour-forms-home__done-disc${pathway === 'sen' ? '' : ' harbour-forms-home__done-disc--referral'}`}
                    aria-hidden="true"
                  >
                    <HarbourIcon name="check" size={16} />
                  </span>
                  <h2 className="harbour-form-card__title">
                    {answersSubmitted
                      ? `All ${rows.length} forms submitted`
                      : `All ${rows.length} forms are answered. Check and submit them.`}
                  </h2>
                </span>
              ) : (
                <h2 className="harbour-form-card__title">{`${doneCount} of ${rows.length} forms done`}</h2>
              )}
              <span className="ml-auto flex-none flex items-center gap-3">
                {allDone && (!canConfirm || usesSubmitPopup) && (
                  <HarbourButton
                    kind="secondary"
                    leadingIcon="download"
                    onClick={() => window.open(`/rms/pupils/${pupil.id}/answers/print`, '_blank', 'noopener')}
                  >
                    Download your answers
                  </HarbourButton>
                )}
                {allDone && canConfirm && usesSubmitPopup ? (
                  // S-1 (5226:131473): the one filled action opens the pop-up.
                  <HarbourButton kind="primary" onClick={() => setSubmitPopupOpen(true)}>
                    Confirm and submit all responses
                  </HarbourButton>
                ) : (
                  <HarbourButton
                    // One filled action per view: while the confirm step is on
                    // offer it is the primary action, so this drops to outline.
                    kind={allDone && !canConfirm ? 'primary' : 'secondary'}
                    onClick={() => navigate(`/rms/pupils/${pupil.id}`)}
                  >
                    {`Back to ${firstName}'s record`}
                  </HarbourButton>
                )}
              </span>
            </div>
            {senWithdrawn && (
              <HarbourNotice kind="info" title="SEND pathway: consent withdrawn by the parent or carer">
                The answers are kept, and you can still read them. Nothing more can be added.
              </HarbourNotice>
            )}
            {allDone ? (
              canConfirm && usesSubmitPopup ? (
                <p className="harbour-body-16">Thank you. You can open any form to review your answers.</p>
              ) : canConfirm ? (
                <div className="flex items-center gap-4">
                  <p className="harbour-body-16">
                    One last step: check your answers, then confirm. Your answers cannot be changed after you confirm.
                  </p>
                  <span className="ml-auto flex-none">
                    <HarbourButton kind="primary" trailingIcon="arrow-right" onClick={() => setConfirming(true)}>
                      Confirm and submit
                    </HarbourButton>
                  </span>
                </div>
              ) : (
                <p className="harbour-body-16">
                  {answersLocked
                    ? 'You’ve submitted your answers, so they’re now locked and can’t be changed. You can still read them and download a copy.'
                    : 'Thank you. You can open any form to review your answers.'}
                </p>
              )
            ) : (
              <div className="flex flex-col gap-2">
                <p className="harbour-body-16">
                  {`${timeLine}Your answers save as you go.`}
                </p>
                <HarbourProgressSegments tone={pathway === 'sen' ? 'sen' : 'referral'} total={rows.length} doneCount={doneCount} />
              </div>
            )}
            <div className="flex flex-col gap-2">
              {rows.map((row, i) => {
                const title = harbourFormTitle(row.formKey, schemas[row.formKey]);
                const route = harbourFormRoute(pupil.id, row.formKey);
                const open = row.open;
                if (row.heldBy) {
                  return (
                    <TeacherHeldFormRow
                      key={row.formKey}
                      row={row}
                      index={i}
                      title={title}
                      route={route}
                      tone={pathway === 'sen' ? 'sen' : 'referral'}
                      submitted={answersSubmitted}
                    />
                  );
                }
                return (
                  <div
                    key={row.formKey}
                    className={[
                      'harbour-form-row',
                      `harbour-form-row--${pathway === 'sen' ? 'sen' : 'referral'}`,
                      row.status === 'done' ? 'harbour-form-row--done' : '',
                      open && row.status !== 'done' ? 'harbour-form-row--open' : '',
                    ].filter(Boolean).join(' ')}
                  >
                    <span className="harbour-form-row__disc" aria-hidden="true">
                      {row.status === 'done' ? <HarbourIcon name="check" size={16} /> : i + 1}
                    </span>
                    <span className="harbour-form-row__text">
                      {/* Once the answers are locked the name is no longer a way
                          in to change them, so it stops looking like one; the
                          View action beside it is the way to read them. */}
                      {open && !readOnly ? (
                        <HarbourTextLink to={route}>{title}</HarbourTextLink>
                      ) : (
                        <span className="harbour-body-16-bold">{title}</span>
                      )}
                      <span className="harbour-form-row__meta">
                        {row.status === 'done' ? 'All questions completed' : rowMeta(row.formKey)}
                      </span>
                    </span>
                    <span className="ml-auto flex items-center gap-3 flex-none">
                      {open ? (
                        <>
                          <HarbourStatusChip
                            status={row.status === 'done' ? 'done' : row.status === 'inProgress' ? 'inProgress' : 'notStarted'}
                            label={row.status === 'done' && answersSubmitted ? 'Submitted' : undefined}
                            tone={row.status === 'done' ? (pathway === 'sen' ? 'sen' : 'referral') : 'neutral'}
                            surface={row.status === 'done' ? 'onWhite' : 'tinted'}
                          />
                          {row.status === 'done' ? (
                            <HarbourTextLink to={`${route}?mode=review`}>
                              {readOnly ? 'View' : 'Review your answers'}
                            </HarbourTextLink>
                          ) : senWithdrawn ? (
                            row.status === 'inProgress' && (
                              <HarbourTextLink to={`${route}?mode=review`}>View</HarbourTextLink>
                            )
                          ) : (
                            <HarbourButton kind="primary" trailingIcon="arrow-right" onClick={() => navigate(route)}>
                              {row.status === 'inProgress' ? 'Continue' : 'Start'}
                            </HarbourButton>
                          )}
                        </>
                      ) : (
                        <span className="harbour-form-row__locked">
                          <HarbourIcon name="lock" size={16} />
                          Locked
                        </span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
          )}
        </div>
        <aside className="harbour-task-body__aside flex flex-col gap-4">
          {!allDone && (
            <HarbourNotice kind="info" title="How this works">
              {needsConfirmStep
                ? 'Answer one form at a time. The next one opens when you finish the one before. Finished forms stay open until you confirm and submit at the end.'
                : 'Answer one form at a time. The next one opens when you finish the one before. Finished forms stay open, so you can change an answer at any time.'}
            </HarbourNotice>
          )}
          {allDone && pathway === 'sen' && (pupil.sen && pupil.sen.reportAvailable ? (
            <>
              <div className="harbour-card rounded-3xl p-6 flex flex-col gap-4">
                <div className="flex items-center gap-3 flex-wrap">
                  <p className="harbour-body-16-bold">{`${firstName}'s profile`}</p>
                  <HarbourStatusChip status="reportAvailable" tone="sen" />
                </div>
                <div className="flex flex-wrap gap-3">
                  <HarbourButton kind="secondary" trailingIcon="arrow-right" onClick={() => navigate(`/rms/patient/${pupil.id}/hfp`)}>
                    View strengths and needs profile
                  </HarbourButton>
                  <HarbourButton
                    kind="secondary"
                    leadingIcon="download"
                    onClick={() => window.open(`/rms/patient/${pupil.id}/hfp/print`, '_blank', 'noopener')}
                  >
                    Download PDF
                  </HarbourButton>
                </div>
              </div>
              <div className="harbour-card rounded-3xl p-6 flex flex-col gap-3">
                <p className="harbour-body-16-bold">What happens next</p>
                <ol className="flex flex-col gap-2">
                  <li className="flex items-start gap-3"><span className="harbour-form-row__disc" aria-hidden="true">1</span><span>{`Read ${firstName}'s profile on your own first`}</span></li>
                  <li className="flex items-start gap-3"><span className="harbour-form-row__disc" aria-hidden="true">2</span><span>Then talk it through with the family</span></li>
                </ol>
              </div>
            </>
          ) : (
            <div className="harbour-card rounded-3xl p-6 flex flex-col gap-2">
              <p className="harbour-body-16-bold">{`${firstName}'s profile`}</p>
              <p className="harbour-body-16">
                {profileWaitLine || `${firstName}'s profile is created when both sets of forms are done.`}
              </p>
            </div>
          ))}
          {allDone && guardianStatus !== 'completed' && (
            <div className="harbour-card rounded-3xl p-6 flex flex-col gap-4">
              <p className="harbour-body-16-bold">What happens next</p>
              <div className="flex items-center gap-3">
                <span className="harbour-body-16 text-[color:var(--harbour-colour-text-muted)]">Parent or carer forms</span>
                <HarbourStatusChip status={guardianChip.status} label={guardianChip.label} tone="neutral" />
              </div>
              {guardian && (
                <div>
                  <HarbourButton kind="secondary" busy={reminderBusy} onClick={sendReminder}>
                    {`Send ${guardian.firstName || 'the parent or carer'} a reminder`}
                  </HarbourButton>
                </div>
              )}
            </div>
          )}
        </aside>
      </div>
      {submitPopupOpen && canConfirm && usesSubmitPopup && (
        <SubmitFormsModal
          count={rows.length}
          nothingHappensLine="Nothing happens until you submit them."
          afterLine={profileWaitLine}
          actionKind="primary"
          busy={confirmBusy}
          error={confirmFailed}
          onConfirm={onConfirmSubmission}
          onClose={() => { setSubmitPopupOpen(false); setConfirmFailed(false); }}
        />
      )}
      {toast && (
        <HarbourToast title={toast.title} onClose={() => setToast(null)}>
          {toast.body}
        </HarbourToast>
      )}
    </HarbourFlyout>
  );
}
